import { useQuery } from "@tanstack/react-query";
import { tgHtmlToPlain, type BotLanguages } from "@kickwin/shared";
import { api, type TreeButton, type TreePage, type Translation } from "./api";

/**
 * Translations in the panel (decision 46). One structure per message; each
 * language only overrides words, keyed by each button's stable `k`.
 */

export function newKey(): string {
  return Math.random().toString(36).slice(2, 10) || "k";
}

/** Gives every button a stable id, so translations can find it. */
export function withKeys(page: TreePage): TreePage {
  return {
    ...page,
    buttons: page.buttons.map((row) =>
      row.map((b): TreeButton => {
        const k = b.k ?? newKey();
        return "page" in b ? { ...b, k, page: withKeys(b.page) } : { ...b, k };
      }),
    ),
  };
}

/** The tree as a player of that language sees it. */
export function localizeTree(root: TreePage, t: Translation | undefined): TreePage {
  if (!t) return root;
  const nodes = t.nodes ?? {};
  const page = (p: TreePage, text?: string, back?: string): TreePage => ({
    ...p,
    text: text || p.text,
    ...(back ? { backLabel: back } : {}),
    buttons: p.buttons.map((row) =>
      row.map((b): TreeButton => {
        const n = b.k ? nodes[b.k] : undefined;
        const label = n?.label || b.text;
        if ("page" in b) return { ...b, text: label, page: page(b.page, n?.text, n?.back) };
        if ("help" in b) return { ...b, text: label, help: n?.reply ? { reply: n.reply } : b.help };
        return { ...b, text: label };
      }),
    ),
  });
  return page(root, t.text);
}

const filled = (value: string | undefined, html: boolean) =>
  !!value && (html ? !!tgHtmlToPlain(value).trim() : !!value.trim());

/** Drops empty words, and languages left with none, before saving. */
export function pruneTranslations(
  i18n: Record<string, Translation> | undefined,
): Record<string, Translation> | undefined {
  if (!i18n) return undefined;
  const out: Record<string, Translation> = {};
  for (const [lang, t] of Object.entries(i18n)) {
    const nodes: NonNullable<Translation["nodes"]> = {};
    for (const [k, n] of Object.entries(t.nodes ?? {})) {
      const kept = {
        ...(filled(n.label, false) ? { label: n.label!.trim() } : {}),
        ...(filled(n.text, true) ? { text: n.text } : {}),
        ...(filled(n.back, false) ? { back: n.back!.trim() } : {}),
        ...(filled(n.reply, true) ? { reply: n.reply } : {}),
      };
      if (Object.keys(kept).length > 0) nodes[k] = kept;
    }
    const kept: Translation = {
      ...(filled(t.text, true) ? { text: t.text } : {}),
      ...(filled(t.description, false) ? { description: t.description!.trim() } : {}),
      ...(Object.keys(nodes).length > 0 ? { nodes } : {}),
    };
    if (Object.keys(kept).length > 0) out[lang] = kept;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export function useLanguages(botId: string | undefined): BotLanguages | undefined {
  const query = useQuery({
    queryKey: ["languages", botId],
    queryFn: () => api.get<BotLanguages>(`/bots/${botId}/languages`),
    enabled: !!botId,
    staleTime: 60_000,
  });
  return query.data;
}
